/*
 * Copyright (c) 2019. Nedaware SL
 */

import {PlacedWidget, WidgetType} from "./widget";
import {WidgetDb} from "./widget-db";
import {SessionService} from "../../session/session.service";

/**
 * Nedaware S.L.
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
export class WidgetGrid {
    get widgets(): PlacedWidget[] {
        return this._widgets.slice();
    }

    get columns(): number {
        return this._columns;
    }

    get rows(): number {
        return this._rows;
    }

    private _widgets: PlacedWidget[] = [];
    private _widgetsDb = new WidgetDb(this._session);

    constructor(
        private _columns: number,
        private _rows: number,
        private _session: SessionService
    ) {
        this._widgetsDb.waitingForDb.subscribe(
            undefined, undefined,
            () => this._widgetsDb.retrieve()
                .then(widgets => this._widgets.splice(0, 0, ...widgets))
        );
    }

    public add(widget: WidgetType, row: number, column: number) {
        if (this.canAdd(widget, row, column)) {
            const placed = new PlacedWidget(widget, row, column, widget.minRows, widget.minCols);
            this._widgetsDb.add(placed);
            this._widgets.push(placed);
        }
    }

    public remove(idx: number): void {
        const removed = this._widgets.splice(idx, 1);
        this._widgetsDb.remove(removed[0]);
    }

    public translate(index: number, offsetRow: number, offsetCol: number): void {
        this.move(index, this._widgets[index].row + offsetRow, this._widgets[index].column + offsetCol);
    }

    public move(index: number, row: number, column: number): void {
        if (this.canMove(index, row, column)) {
            const moved = this._widgets[index];
            moved.column = column;
            moved.row = row;
            this._widgetsDb.update(moved);
        }
    }

    public canAdd(widget: WidgetType, row: number, column: number, rows?: number, cols?: number) {
        rows = rows || widget.minRows;
        cols = cols || widget.minCols;
        const intersects = ([a, b], [x, y]) => x <= b && y >= a;
        const placed = new PlacedWidget(widget, row, column, rows, cols);
        if (row <= 0 || column <= 0) {
            return false;
        }
        if (this.rows && placed.rowRange.some(r => r > this._rows)) {
            return false;
        }
        if (this.columns && placed.colRange.some(r => r > this._columns)) {
            return false;
        }
        return !this._widgets.some(w =>
            intersects(placed.rowRange, w.rowRange) && intersects(placed.colRange, w.colRange));
    }

    public canMove(index: number, row: number, column: number) {
        const moved = this._widgets.splice(index, 1)[0];
        const canMove = this.canAdd(moved.component, row, column, moved.rows, moved.cols);
        this._widgets.splice(index, 0, moved);
        return canMove;
    }
}
